<template>
	<div class="package-row" :class="{ 'with-divider': showDivider }" @click="$emit('onPackageToggle', packageItem)">
		<div class="package-row-details">
			<ProviderIcon :iconUrl="packageItem.icon" :providerShortName="providerShortName" />

			<span class="package-row-details__name">{{ packageItem.clearName }}</span>
		</div>
		<div class="package-row-actions">
			<button
				v-if="badgeLabel && showBadge"
				class="package-row-actions__badge"
				:class="{ 'package-row-actions__badge--active': isBadgeActive }"
				type="button"
			>
				{{ badgeLabel }}
			</button>
			<CheckboxInput class="package-row-actions__checkbox" :modelValue="packageItem.selected" />
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { Bundle, Package } from '@/@types/graphql-types';
import CheckboxInput from '@/ui-components/CheckboxInput.vue';

const props = withDefaults(
	defineProps<{
		packageItem: Package | Bundle;
		showBadge?: boolean;
		showDivider?: boolean;
	}>(),
	{
		showBadge: true,
		showDivider: true,
	}
);

defineEmits<{
	(e: 'onPackageToggle', packageItem: Package | Bundle): void;
}>();

const { t } = useI18n();

const badgeLabel = computed(() => {
	if (props.packageItem.__typename === 'Package') {
		const { bundles = [], addons = [] } = props.packageItem;
		if (bundles.length > 0 || addons.length > 0) {
			return t('WEBAPP_PACKAGES_EXTRAS');
		}
	}
	return null;
});

const isBadgeActive = computed(() => {
	if (props.packageItem.__typename === 'Package') {
		const shouldBeActive = props.packageItem.selected;
		const hasActiveBundles = props.packageItem.bundles.some(b => b.selected);
		const hasSelectedAddons = props.packageItem.addons.some(a => a.selected);
		return shouldBeActive && (hasActiveBundles || hasSelectedAddons);
	}
	return false;
});

const providerShortName = computed(() => props.packageItem.shortName);
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

.package-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 16px;
	cursor: pointer;

	&.with-divider {
		border-bottom: 1px solid $jw-color-darkgray--dark;
	}

	&:hover {
		background: $jw-color-darkgray;
	}

	&-details {
		display: flex;
		align-items: center;
		gap: 15px;

		:deep(img.provider-icon.square) {
			height: 38px;
			width: 38px;
			min-width: 38px;
		}
	}

	&-actions {
		display: flex;
		align-items: center;

		&__badge {
			display: block;
			margin-inline-start: 10px;
			height: 20px;
			background-color: transparent;
			border-radius: 6px;
			border: 1px solid $jw-color-darkgray;
			color: $jw-color-mediumblue;
			font-size: 10px;
			font-weight: bold;
			text-transform: capitalize;

			&--active {
				background-color: $jw-color-gold;
				color: $jw-color-darkgray;
				border: none;
			}
		}

		&__checkbox {
			margin-inline-start: 24px;
			pointer-events: none;
		}
	}
}
</style>
